import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import { apiRequest } from "../../../common/api/apiClient.js";
import type { MailAccountDTO, MailMessageDTO, MailMessagePageDTO, OutgoingMailRequest, UpdateMailMessageRequest } from "../../../common/api/types.js";
import type { MailView } from "../mailTypes.js";

const root = ["mail"] as const;
export const mailAccountsKey = [...root, "accounts"] as const;
const messagesKey = (accountId: string, view: MailView, query: string) => [...root, "messages", accountId, view, query] as const;
const messageKey = (accountId: string, messageId: string) => [...root, "message", accountId, messageId] as const;

/** Wie oft nach neuer Post gefragt wird. Push über JMAP folgt später. */
const POLL_MS = 60_000;

export function useMailAccounts(): UseQueryResult<MailAccountDTO[], Error> {
  return useQuery({ queryKey: mailAccountsKey, queryFn: () => apiRequest<MailAccountDTO[]>("/mail/accounts"), refetchInterval: POLL_MS, retry: false });
}

export function useMailMessages(accountId: string | null, view: MailView, query: string): UseQueryResult<MailMessagePageDTO, Error> {
  const params = new URLSearchParams({ view, limit: "100", ...(query ? { query } : {}) });
  return useQuery({
    queryKey: messagesKey(accountId ?? "", view, query),
    queryFn: () => apiRequest<MailMessagePageDTO>(`/mail/accounts/${encodeURIComponent(accountId ?? "")}/messages?${params.toString()}`),
    enabled: Boolean(accountId),
    refetchInterval: POLL_MS,
    placeholderData: keepPreviousData,
  });
}

export function useMailMessage(accountId: string | null, messageId: string | null): UseQueryResult<MailMessageDTO, Error> {
  const client = useQueryClient();
  return useQuery({
    queryKey: messageKey(accountId ?? "", messageId ?? ""),
    queryFn: async () => {
      const message = await apiRequest<MailMessageDTO>(`/mail/accounts/${encodeURIComponent(accountId ?? "")}/messages/${encodeURIComponent(messageId ?? "")}`);
      // Lesen markiert als gelesen: Liste und Zähler in der Sidebar nachziehen.
      void client.invalidateQueries({ queryKey: [...root, "messages", accountId ?? ""] });
      void client.invalidateQueries({ queryKey: mailAccountsKey });
      return message;
    },
    enabled: Boolean(accountId && messageId),
  });
}

function useRefreshMail() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: root });
}

export function useUpdateMailMessage() {
  const refresh = useRefreshMail();
  return useMutation({
    mutationFn: ({ accountId, messageId, body }: { accountId: string; messageId: string; body: UpdateMailMessageRequest }) =>
      apiRequest<void>(`/mail/accounts/${encodeURIComponent(accountId)}/messages/${encodeURIComponent(messageId)}`, { method: "PATCH", body }),
    onSuccess: refresh,
  });
}

export function useDeleteMailMessage() {
  const refresh = useRefreshMail();
  return useMutation({
    mutationFn: ({ accountId, messageId }: { accountId: string; messageId: string }) =>
      apiRequest<void>(`/mail/accounts/${encodeURIComponent(accountId)}/messages/${encodeURIComponent(messageId)}`, { method: "DELETE" }),
    onSuccess: refresh,
  });
}

export function useSaveDraft() {
  const refresh = useRefreshMail();
  return useMutation({
    mutationFn: ({ accountId, body }: { accountId: string; body: OutgoingMailRequest }) =>
      apiRequest<{ id: string }>(`/mail/accounts/${encodeURIComponent(accountId)}/drafts`, { method: "POST", body }),
    onSuccess: refresh,
  });
}

export function useSendMail() {
  const refresh = useRefreshMail();
  return useMutation({
    mutationFn: ({ accountId, body }: { accountId: string; body: OutgoingMailRequest }) =>
      apiRequest<{ id: string }>(`/mail/accounts/${encodeURIComponent(accountId)}/send`, { method: "POST", body }),
    onSuccess: refresh,
  });
}
